import React, { useState } from 'react'
import ChallengeSubmitForm from '../challenges/ChallengeSubmitForm'
import CompanySubmitForm from '../companies/CompanySubmitForm'
import UserSubmitForm from '../users/UserSubmitForm'
import Steps from 'components/shared/Steps'
import ErrorEmailTaken from 'components/shared/ErrorEmailTaken'
import { firstOrNull, scrollToId } from 'utils/helpers'
import ChallengeService from 'services/challenge.service'
import User from 'models/user'
import { IChallenge } from 'models/challenge'
import Company from 'models/company'
import triggerGAEvent from 'utils/triggerGAEvent'

const openChallengeSpaceId = process.env.OPEN_CHALLENGE_SPACE_ID

interface ChallengeSpaceSubmitChallengeStepFormProps {
	challengeSpace: any
	organisation: any
}
interface FormStep {
	key: 'challenge' | 'company' | 'user'
	title: string
	valid: boolean
}

interface FormState {
	loading: boolean
	submitted: boolean
	errors: {}
	showOpenChallenge?: boolean
	forceOpenChallengeType?: string
	userFormData: User
	companyFormData: Company
	challengeFormData: IChallenge
	verifiable?: any
}

const ChallengeSpaceSubmitChallengeStepForm = (
	props: ChallengeSpaceSubmitChallengeStepFormProps
) => {
	const validatorRef: React.RefObject<any> = React.createRef()
	const { challengeSpace, organisation } = props
	const [currentStep, setCurrentStep] = useState<number>(0)

	const mcOnlyGlobalChallengeSpace = challengeSpace.id === openChallengeSpaceId

	const forceOpenChallengeType = mcOnlyGlobalChallengeSpace
		? 'global'
		: challengeSpace?.attributes?.isOpenChallengeSpace
		? 'organisation'
		: 'private'

	const [formState, setFormState] = useState<FormState>({
		loading: false,
		submitted: false,
		errors: {},
		// Show submit as open challenge when space is open challenge space and organisation setting is set to 'global'
		showOpenChallenge:
			!mcOnlyGlobalChallengeSpace &&
			organisation?.attributes?.allowGlobalOpenChallenge &&
			challengeSpace?.attributes?.isOpenChallengeSpace,
		forceOpenChallengeType: forceOpenChallengeType ?? null,
		challengeFormData: {
			title: '',
			summary: '',
			teamCountPreference: 1,
			description: '',
			openChallengeType: forceOpenChallengeType,
			answers: {},
		},
		companyFormData: {
			cityName: '',
			description: '',
			employeeCount: '',
			foundedAt: '',
			houseNumber: '',
			houseNumberExtension: '',
			iban: '',
			logoUrl: '',
			name: '',
			ownerId: '',
			postalCode: '',
			streetName: '',
			websiteUrl: '',
		},
		userFormData: {
			email: '',
			linkedinUrl: '',
			phoneNumber: '',
			firstname: '',
			lastname: '',
			password: '',
		},
	})

	const [steps, setSteps] = useState<FormStep[]>([
		{
			key: 'challenge',
			title: 'Your challenge',
			valid: false,
		},
		{
			key: 'company',
			title: 'Your company',
			valid: false,
		},
		{
			key: 'user',
			title: 'Register account',
			valid: false,
		},
	])

	const handleDataChange = (
		type: 'companyFormData' | 'challengeFormData' | 'userFormData',
		formData: IChallenge | Company | User,
		valid: boolean
	) => {
		setFormState({ ...formState, [type]: formData })
		const newSteps = steps
		newSteps[currentStep].valid = valid
		setSteps(newSteps)
	}

	const StepForms = Object.freeze({
		0: (
			<ChallengeSubmitForm
				showGenerateAiButton={true}
				onDataChange={handleDataChange}
				showOpenChallenge={formState.showOpenChallenge}
				data={formState.challengeFormData}
				ref={validatorRef}
			/>
		),
		1: (
			<CompanySubmitForm
				onDataChange={handleDataChange}
				data={formState.companyFormData}
				ref={validatorRef}
			/>
		),
		2: (
			<UserSubmitForm
				onDataChange={handleDataChange}
				data={formState.userFormData}
				ref={validatorRef}
			/>
		),
	})

	const prevStep = () => {
		setCurrentStep(currentStep > 0 ? currentStep - 1 : 0)
		scrollToId('formTop')
	}

	const createCompanyChallenge = () => {
		const userData: User = formState.userFormData
		const challengeData: IChallenge = formState.challengeFormData
		const companyData: Company = formState.companyFormData

		const payload = ChallengeService.prepareCompanyChallengePayload(
			userData,
			challengeData,
			companyData,
			organisation.id,
			challengeSpace.id
		)

		ChallengeService.createCompanyChallenge(payload, {
			authenticate: false,
		})
			.then((response) => {
				setFormState({
					...formState,
					submitted: true,
					loading: false,
					verifiable: firstOrNull(response?.verifyRepsonse),
				})
				scrollToId('submitSection')
			})
			.then(() => {
				triggerGAEvent.call(this, {
					page: window.location.href,
					event: 'challengeRegister&Submit',
					params: {
						spaceTitle: challengeSpace.attributes.title,
					},
				})
			})
			.catch((err) => {
				setFormState({
					...formState,
					errors: err.errors,
					loading: false,
				})
				scrollToId('submitSection')
			})
	}

	const nextStep = () => {
		const innerValid = validatorRef?.current?.validator?.allValid() ?? true

		if (!innerValid || !steps[currentStep].valid) {
			validatorRef.current?.validator?.showMessages()
			return
		}

		if (currentStep < steps.length - 1) {
			setCurrentStep(currentStep + 1)
			scrollToId('formTop')
		} else {
			// dispatch events
			setFormState({ ...formState, loading: true })
			createCompanyChallenge()
		}
	}

	return (
		<>
			<Steps steps={steps} activeStep={currentStep} />

			<div className="border -mt-px rounded-b px-6 pb-6 bg-white" id="formTop">
				{formState.submitted ? (
					<>
						<h2 className="text-2xl mb-4 font-sans font-bold px-8 pt-8">
							Check your mail!
						</h2>

						{formState.verifiable && (
							<p className="mb-4 px-8 text-lg font-bold text-red-500">
								{formState.verifiable.attributes.message}
							</p>
						)}

						<p className="mb-4 px-8 pb-4 text-lg">
							Thanks for submitting! Please{' '}
							<strong>check your email (also your spam folder)</strong> with a
							link to confirm your account. You will receive a notification when
							your challenge is approved.
						</p>
					</>
				) : (
					<>
						{StepForms[currentStep]}
						{/* Print out only e-mail errror */}
						{formState.errors['user_email'] && (
							<div className="mb-2">
								<ErrorEmailTaken />
							</div>
						)}
						<div className="flex items-center justify-between">
							{currentStep > 0 && (
								<button
									className="bg-gray-300 text-black hover:bg-gray-400 mr-2"
									onClick={prevStep}
									type="submit"
								>
									&lsaquo; Previous step
								</button>
							)}
							<button
								className="bg-yellow-400 text-black transition-colors duration-300 hover:bg-yellow-300"
								onClick={nextStep}
								type="button"
							>
								{currentStep === 2 ? 'Submit' : 'Next step'} &rsaquo;
							</button>
						</div>
					</>
				)}
			</div>
		</>
	)
}

export default ChallengeSpaceSubmitChallengeStepForm
